/**
 * Icon set, matching the sidebar and stat tiles in the screen design.
 * Line art on `currentColor`, 24×24, so a theme recolours them for free.
 */

type IconProps = { className?: string; size?: number };

function svg(path: React.ReactNode, { className = '', size = 18 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {path}
    </svg>
  );
}

export const IconDashboard = (p: IconProps) =>
  svg(
    <>
      <path d="M3 10.2 12 3.5l9 6.7" />
      <path d="M5.2 9v10.5h13.6V9" />
      <path d="M9.6 19.5v-6h4.8v6" />
    </>,
    p,
  );

export const IconPatients = (p: IconProps) =>
  svg(
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M2.8 20c0-3.4 2.8-6.2 6.2-6.2s6.2 2.8 6.2 6.2" />
      <path d="M16.2 5.4a3.2 3.2 0 0 1 0 6.1M17.6 13.9c2.1.6 3.6 2.5 3.6 4.8" />
    </>,
    p,
  );

export const IconLabs = (p: IconProps) =>
  svg(
    <>
      <path d="M9 3h6M10 3v6.2L5.6 17a3.2 3.2 0 0 0 2.8 4.8h7.2a3.2 3.2 0 0 0 2.8-4.8L14 9.2V3" />
      <path d="M7.4 14.6h9.2" />
    </>,
    p,
  );

export const IconReports = (p: IconProps) =>
  svg(
    <>
      <path d="M14 2.8H6.8A1.8 1.8 0 0 0 5 4.6v14.8a1.8 1.8 0 0 0 1.8 1.8h10.4a1.8 1.8 0 0 0 1.8-1.8V7.8z" />
      <path d="M14 2.8v5h5" />
      <path d="M8.6 13h6.8M8.6 16.6h4.4" />
    </>,
    p,
  );

export const IconAnalytics = (p: IconProps) =>
  svg(
    <>
      <path d="M3.5 20.5h17" />
      <path d="M6.6 20.5v-6M11 20.5V8.8M15.4 20.5v-8.4M19.8 20.5V5.2" />
    </>,
    p,
  );

export const IconSettings = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="12" r="2.9" />
      <path d="M19.2 14.4a1.6 1.6 0 0 0 .32 1.76l.06.06a1.9 1.9 0 1 1-2.7 2.7l-.06-.06a1.6 1.6 0 0 0-1.76-.32 1.6 1.6 0 0 0-.97 1.47v.17a1.9 1.9 0 1 1-3.8 0v-.09a1.6 1.6 0 0 0-1.05-1.47 1.6 1.6 0 0 0-1.76.32l-.06.06a1.9 1.9 0 1 1-2.7-2.7l.06-.06a1.6 1.6 0 0 0 .32-1.76 1.6 1.6 0 0 0-1.47-.97H3.4a1.9 1.9 0 1 1 0-3.8h.09a1.6 1.6 0 0 0 1.47-1.05 1.6 1.6 0 0 0-.32-1.76l-.06-.06a1.9 1.9 0 1 1 2.7-2.7l.06.06a1.6 1.6 0 0 0 1.76.32h.08a1.6 1.6 0 0 0 .97-1.47V3.4a1.9 1.9 0 1 1 3.8 0v.09a1.6 1.6 0 0 0 .97 1.47 1.6 1.6 0 0 0 1.76-.32l.06-.06a1.9 1.9 0 1 1 2.7 2.7l-.06.06a1.6 1.6 0 0 0-.32 1.76v.08a1.6 1.6 0 0 0 1.47.97h.17a1.9 1.9 0 1 1 0 3.8h-.09a1.6 1.6 0 0 0-1.47.97z" />
    </>,
    p,
  );

export const IconUpload = (p: IconProps) =>
  svg(
    <>
      <path d="M12 16V4.6M7.8 8.8 12 4.6l4.2 4.2" />
      <path d="M3.6 15.4v2.8a2.2 2.2 0 0 0 2.2 2.2h12.4a2.2 2.2 0 0 0 2.2-2.2v-2.8" />
    </>,
    p,
  );

export const IconTrend = (p: IconProps) =>
  svg(
    <>
      <path d="M3.5 16.4l5.2-5.2 3.4 3.4 6.6-6.6" />
      <path d="M14.8 8h3.9v3.9" />
    </>,
    p,
  );

export const IconHeart = (p: IconProps) =>
  svg(
    <path d="M12 20.4s-7.6-4.6-7.6-9.8a4.4 4.4 0 0 1 7.6-3 4.4 4.4 0 0 1 7.6 3c0 5.2-7.6 9.8-7.6 9.8z" />,
    p,
  );

export const IconDownload = (p: IconProps) =>
  svg(
    <>
      <path d="M12 4.6V16M7.8 11.8 12 16l4.2-4.2" />
      <path d="M3.6 18.2v0a2.2 2.2 0 0 0 2.2 2.2h12.4a2.2 2.2 0 0 0 2.2-2.2" />
    </>,
    p,
  );

export const IconCheck = (p: IconProps) =>
  svg(<path d="M4.8 12.6 9.4 17.2 19.2 6.8" />, p);

export const IconAlert = (p: IconProps) =>
  svg(
    <>
      <path d="M12 3.6 21 19.4H3z" />
      <path d="M12 9.4v4.4M12 16.6h.01" />
    </>,
    p,
  );

export const IconBody = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="5" r="2.4" />
      <path d="M12 7.4v6.4M6.6 10.2h10.8M9.4 20.6l1.4-6.8M14.6 20.6l-1.4-6.8" />
    </>,
    p,
  );

export const IconLogout = (p: IconProps) =>
  svg(
    <>
      <path d="M9.6 20.4H5.8A1.8 1.8 0 0 1 4 18.6V5.4a1.8 1.8 0 0 1 1.8-1.8h3.8" />
      <path d="M15.6 16.4 20 12l-4.4-4.4M20 12H9.2" />
    </>,
    p,
  );

export const IconChevron = (p: IconProps) => svg(<path d="M9 5.6 15.4 12 9 18.4" />, p);

export const IconSearch = (p: IconProps) =>
  svg(
    <>
      <circle cx="10.6" cy="10.6" r="6.4" />
      <path d="M15.4 15.4 20.4 20.4" />
    </>,
    p,
  );

export const IconMenu = (p: IconProps) =>
  svg(<path d="M4 7h16M4 12h16M4 17h16" />, p);

export const IconClose = (p: IconProps) => svg(<path d="M6 6l12 12M18 6L6 18" />, p);

export const IconPlus = (p: IconProps) => svg(<path d="M12 5v14M5 12h14" />, p);

/* ------------------------------------------------------------------ *
 * The rest of the mockup set, 23 September. Same 1.7 thin line as above,
 * because the spec asks that "all icons should use the same thin-line style".
 * ------------------------------------------------------------------ */

export const IconBell = (p: IconProps) =>
  svg(
    <>
      <path d="M6.2 16.6V11a5.8 5.8 0 0 1 11.6 0v5.6l1.6 2H4.6l1.6-2Z" />
      <path d="M10 20.4a2.2 2.2 0 0 0 4 0" />
    </>,
    p,
  );

export const IconClipboard = (p: IconProps) =>
  svg(
    <>
      <rect x="5" y="4.6" width="14" height="16.4" rx="1.8" />
      <path d="M9 4.6V3.4h6v1.2M8.6 10.2h6.8M8.6 13.8h6.8M8.6 17.4h4" />
    </>,
    p,
  );

export const IconMedKit = (p: IconProps) =>
  svg(
    <>
      <rect x="3.6" y="7" width="16.8" height="13" rx="1.8" />
      <path d="M9 7V4.8h6V7M12 10.6v5.8M9.1 13.5h5.8" />
    </>,
    p,
  );

export const IconMessage = (p: IconProps) =>
  svg(
    <>
      <rect x="3.8" y="4.4" width="16.4" height="15.2" rx="1.8" />
      <path d="M7.6 9.2h8.8M7.6 12.8h5.6" />
    </>,
    p,
  );

export const IconHelp = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M9.6 9.6a2.5 2.5 0 0 1 4.8.9c0 1.7-2.4 2.2-2.4 3.7M12 17h.01" />
    </>,
    p,
  );

export const IconLeaf = (p: IconProps) =>
  svg(
    <>
      <path d="M5 19c0-8.4 5.4-13.8 14.6-14.6C19 13.6 13.4 19 5 19Z" />
      <path d="M5 19 13 11" />
    </>,
    p,
  );

export const IconTarget = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="12" r="8.4" />
      <circle cx="12" cy="12" r="4.8" />
      <circle cx="12" cy="12" r="1.4" />
    </>,
    p,
  );

export const IconSyringe = (p: IconProps) =>
  svg(
    <>
      <path d="m15.8 3.6 4.6 4.6M18.1 5.9l-9.9 9.9-3.2.7.7-3.2 9.9-9.9M10.3 9.3l1.6 1.6M8.2 11.4l1.6 1.6M4.6 19.4l2-2" />
    </>,
    p,
  );

export const IconMolecule = (p: IconProps) =>
  svg(
    <>
      <circle cx="7" cy="7" r="2.4" />
      <circle cx="17" cy="9" r="2.4" />
      <circle cx="9" cy="17.4" r="2.4" />
      <path d="M9.2 7.7 14.7 8.6M8.1 9.2l.5 5.8M15.2 10.8l-4.4 5" />
    </>,
    p,
  );

export const IconPill = (p: IconProps) =>
  svg(
    <>
      <rect x="3.4" y="8.4" width="17.2" height="7.2" rx="3.6" transform="rotate(-45 12 12)" />
      <path d="m9.4 9.4 5.2 5.2" />
    </>,
    p,
  );

export const IconCalendar = (p: IconProps) =>
  svg(
    <>
      <rect x="4" y="5.4" width="16" height="14.6" rx="1.8" />
      <path d="M4 10h16M8.4 3.6v3.4M15.6 3.6v3.4M8.4 13.6h.01M12 13.6h.01M15.6 13.6h.01M8.4 16.8h.01M12 16.8h.01" />
    </>,
    p,
  );

export const IconShield = (p: IconProps) =>
  svg(
    <>
      <path d="M12 3.4 19 6v5.6c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6l7-2.6Z" />
      <path d="m9 12.2 2.1 2.1 4-4.2" />
    </>,
    p,
  );

export const IconFlag = (p: IconProps) =>
  svg(<path d="M5.6 20.6V4.2M5.6 4.8h11.2l-2.2 4 2.2 4H5.6" />, p);

export const IconBulb = (p: IconProps) =>
  svg(
    <>
      <path d="M9.4 17.6h5.2M10 20.6h4" />
      <path d="M8.6 14.6a6 6 0 1 1 6.8 0c-.6.5-.8 1.2-.8 1.9v1.1H9.4v-1.1c0-.7-.2-1.4-.8-1.9Z" />
    </>,
    p,
  );

export const IconQuote = (p: IconProps) =>
  svg(
    <>
      <path d="M5 17.6c1.8-.8 3.6-2.6 3.6-5.6V8.4H4.8V12h3.8" />
      <path d="M14.2 17.6c1.8-.8 3.6-2.6 3.6-5.6V8.4H14V12h3.8" />
    </>,
    p,
  );

export const IconPhone = (p: IconProps) =>
  svg(
    <path d="M6.6 3.8h3l1.4 4-2 1.4a11 11 0 0 0 5.8 5.8l1.4-2 4 1.4v3a1.8 1.8 0 0 1-2 1.8A16.4 16.4 0 0 1 4.8 5.8a1.8 1.8 0 0 1 1.8-2Z" />,
    p,
  );

export const IconArrowRight = (p: IconProps) => svg(<path d="M4.6 12h14.6M13.6 6.4 19.2 12l-5.6 5.6" />, p);

export const IconArrowLeft = (p: IconProps) => svg(<path d="M19.4 12H4.8M10.4 6.4 4.8 12l5.6 5.6" />, p);

export const IconArrowUp = (p: IconProps) => svg(<path d="M12 19.4V4.8M6.4 10.4 12 4.8l5.6 5.6" />, p);

export const IconArrowDown = (p: IconProps) => svg(<path d="M12 4.6v14.6M6.4 13.6 12 19.2l5.6-5.6" />, p);

export const IconChevronDown = (p: IconProps) => svg(<path d="M5.6 9 12 15.4 18.4 9" />, p);

export const IconPencil = (p: IconProps) =>
  svg(<path d="M4.4 19.6 5 16l10.8-10.8a2.1 2.1 0 0 1 3 3L8 19l-3.6.6ZM14 7l3 3" />, p);

export const IconSend = (p: IconProps) =>
  svg(<path d="M20.4 3.6 3.6 10.4l7 3 3 7 6.8-16.8ZM10.6 13.4l3.8-3.8" />, p);

export const IconUsers = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="8" r="3" />
      <circle cx="5.6" cy="10" r="2.2" />
      <circle cx="18.4" cy="10" r="2.2" />
      <path d="M6.6 19.6a5.4 5.4 0 0 1 10.8 0M2.6 18.2a3.6 3.6 0 0 1 4.4-3.2M21.4 18.2a3.6 3.6 0 0 0-4.4-3.2" />
    </>,
    p,
  );

export const IconBars = (p: IconProps) =>
  svg(<path d="M5 20V13.4M10 20V9.4M15 20V5.4M20 20V11.4" />, p);

export const IconUser = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="8" r="3.8" />
      <path d="M4.6 20.4a7.4 7.4 0 0 1 14.8 0" />
    </>,
    p,
  );

export const IconBook = (p: IconProps) =>
  svg(<path d="M12 6.2C10 4.8 7 4.4 3.8 4.6v13.6c3.2-.2 6.2.2 8.2 1.6 2-1.4 5-1.8 8.2-1.6V4.6c-3.2-.2-6.2.2-8.2 1.6ZM12 6.2v13.6" />, p);

export const IconDumbbell = (p: IconProps) =>
  svg(<path d="M3.6 12h16.8M6.6 8.4v7.2M4.2 9.8v4.4M17.4 8.4v7.2M19.8 9.8v4.4" />, p);

export const IconScale = (p: IconProps) =>
  svg(
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M8.6 9.4a4.6 4.6 0 0 1 6.8 0L12 12.2 8.6 9.4Z" />
    </>,
    p,
  );

export const IconTrophy = (p: IconProps) =>
  svg(
    <path d="M8 4.4h8v5a4 4 0 0 1-8 0v-5ZM8 6.2H4.8a3.2 3.2 0 0 0 3.4 3.8M16 6.2h3.2a3.2 3.2 0 0 1-3.4 3.8M12 13.4v3.4M8.6 20h6.8M9.6 20l.6-3.2h3.6l.6 3.2" />,
    p,
  );

export const IconNote = (p: IconProps) =>
  svg(
    <>
      <path d="M6 3.8h8.4L18 7.4v12.8H6V3.8Z" />
      <path d="M14.2 3.8v3.8H18M8.8 12h6.4M8.8 15.6h6.4" />
    </>,
    p,
  );

export const IconInfo = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M12 11v5.2M12 7.8h.01" />
    </>,
    p,
  );

export const IconBandage = (p: IconProps) =>
  svg(
    <>
      <rect x="2.8" y="8.2" width="18.4" height="7.6" rx="3.8" transform="rotate(-45 12 12)" />
      <path d="M10.6 10.6h.01M13.4 13.4h.01M10.6 13.4h.01M13.4 10.6h.01" />
    </>,
    p,
  );

export const IconChevronLeft = (p: IconProps) => svg(<path d="M15 5.6 8.6 12 15 18.4" />, p);

export const IconDots = (p: IconProps) =>
  svg(<path d="M5.6 12h.01M12 12h.01M18.4 12h.01" />, p);

export const IconFlask = (p: IconProps) =>
  svg(
    <>
      <path d="M9.4 3.4h5.2M10.2 3.4v5.8L5.4 17.6a2.2 2.2 0 0 0 1.9 3.2h9.4a2.2 2.2 0 0 0 1.9-3.2l-4.8-8.4V3.4" />
      <path d="M7.6 14.2h8.8" />
    </>,
    p,
  );
